/**
 * ImageComparison - Componente para Comparar Imágenes Antes/Después
 * 
 * Permite comparar visualmente el estado inicial vs final del vehículo
 * con slider interactivo y vista lado a lado.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { 
  FaChevronLeft, 
  FaChevronRight, 
  FaExpandArrowsAlt,
  FaSync,
  FaEye,
  FaEyeSlash,
  FaDownload,
  FaInfoCircle,
  FaSearchPlus,
  FaExpand,
  FaTimes
} from 'react-icons/fa';
import ImageViewer from '../common/ImageViewer';
import Switch from '../common/Switch';

// Hook para detectar dispositivo móvil
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768 || 'ontouchstart' in window);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
};

const ImageComparison = ({
  imagenesAntes = [],
  imagenesDespues = [],
  className = ''
}) => {
  const [currentAntes, setCurrentAntes] = useState(0);
  const [currentDespues, setCurrentDespues] = useState(0);
  const [comparisonMode, setComparisonMode] = useState('sideBySide'); // 'slider', 'sideBySide'
  const [sliderPosition, setSliderPosition] = useState(50);
  const [showOverlay, setShowOverlay] = useState(true);
  const [syncNavigation, setSyncNavigation] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Estados para el visor de imágenes
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerImages, setViewerImages] = useState([]);
  const [viewerIndex, setViewerIndex] = useState(0);
  
  const containerRef = useRef(null);
  const isDragging = useRef(false);
  const animationFrameRef = useRef(null);
  const lastPositionRef = useRef(50);
  const isMobile = useIsMobile();

  // Función para toggle fullscreen
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => {
      const newFullscreen = !prev;
      
      // En móvil, manejar mejor el fullscreen
      if (newFullscreen && isMobile) {
        // Intentar esconder la barra de direcciones en móvil
        setTimeout(() => {
          window.scrollTo(0, 1);
        }, 100);
      }
      
      return newFullscreen;
    });
  }, [isMobile]);

  // Manejar ESC para salir de fullscreen
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    if (isFullscreen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevenir scroll en el body cuando está en fullscreen
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (!isFullscreen) {
        document.body.style.overflow = '';
      }
    };
  }, [isFullscreen]);

  // Manejar movimiento del slider con throttling usando requestAnimationFrame
  const handleSliderMove = useCallback((e) => {
    if (!isDragging.current || !containerRef.current) return;
    
    e.preventDefault();
    
    // Cancelar frame anterior si existe
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    // Usar requestAnimationFrame para throttling suave
    animationFrameRef.current = requestAnimationFrame(() => {
      if (!containerRef.current) return;
      
      const rect = containerRef.current.getBoundingClientRect();
      
      // Obtener coordenada X del evento (mouse o touch)
      const clientX = e.type.startsWith('touch') ? e.touches[0]?.clientX : e.clientX;
      if (clientX === undefined) return;
      
      const x = clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
      
      // Solo actualizar si la diferencia es significativa (reduce renders innecesarios)
      if (Math.abs(percentage - lastPositionRef.current) > 0.5) {
        lastPositionRef.current = percentage;
        setSliderPosition(percentage);
      }
    });
  }, []);

  const handleSliderStart = useCallback((e) => {
    e.preventDefault();
    isDragging.current = true;
    
    // Agregar clase para mejorar feedback visual
    if (containerRef.current) {
      containerRef.current.classList.add('dragging');
    }
    
    // Cambiar cursor globalmente durante el drag
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const handleSliderEnd = useCallback(() => {
    isDragging.current = false;
    
    // Cancelar cualquier animationFrame pendiente
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    
    // Remover clase visual
    if (containerRef.current) {
      containerRef.current.classList.remove('dragging');
    }
    
    // Restaurar cursor
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  // Manejar click directo para posicionar el slider
  const handleSliderClick = useCallback((e) => {
    if (!containerRef.current || isDragging.current) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    requestAnimationFrame(() => {
      if (!containerRef.current) return;
      
      const rect = containerRef.current.getBoundingClientRect();
      const clientX = e.type.startsWith('touch') ? e.touches[0]?.clientX : e.clientX;
      if (clientX === undefined) return;
      
      const x = clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
      
      lastPositionRef.current = percentage;
      setSliderPosition(percentage);
    });
  }, []);

  // Efectos para eventos globales del mouse y touch
  useEffect(() => {
    const handleGlobalMove = (e) => {
      if (isDragging.current) {
        handleSliderMove(e);
      }
    };

    const handleGlobalEnd = (e) => {
      if (isDragging.current) {
        e.preventDefault();
        handleSliderEnd();
      }
    };

    // Agregar listeners globales con passive: false para poder usar preventDefault
    document.addEventListener('mousemove', handleGlobalMove, { passive: false });
    document.addEventListener('mouseup', handleGlobalEnd, { passive: false });
    document.addEventListener('touchmove', handleGlobalMove, { passive: false });
    document.addEventListener('touchend', handleGlobalEnd, { passive: false });
    
    // También escuchar mouseleave para casos edge
    document.addEventListener('mouseleave', handleGlobalEnd);

    return () => {
      document.removeEventListener('mousemove', handleGlobalMove);
      document.removeEventListener('mouseup', handleGlobalEnd);
      document.removeEventListener('touchmove', handleGlobalMove);
      document.removeEventListener('touchend', handleGlobalEnd);
      document.removeEventListener('mouseleave', handleGlobalEnd);
      
      // Cancelar animationFrame pendiente
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      
      // Limpiar estilos si se desmonta durante drag
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [handleSliderMove, handleSliderEnd]);

  // Si no hay imágenes en ambas categorías
  if (!imagenesAntes.length || !imagenesDespues.length) {
    return (
      <div className={`text-center py-8 ${className}`}>
        <FaInfoCircle className="mx-auto text-gray-500 mb-3" size={32} />
        <p className="text-gray-400">
          Se necesitan imágenes antes Y después para realizar la comparación
        </p>
        <div className="mt-4 grid grid-cols-2 gap-4 text-sm text-gray-500">
          <div>
            <span className="text-blue-400">Antes:</span> {imagenesAntes.length} imagen{imagenesAntes.length !== 1 ? 'es' : ''}
          </div>
          <div>
            <span className="text-green-400">Después:</span> {imagenesDespues.length} imagen{imagenesDespues.length !== 1 ? 'es' : ''}
          </div>
        </div>
      </div>
    );
  }

  // Navegación sincronizada
  const navigateImages = (direction) => {
    if (syncNavigation) {
      // Navegar ambas galerías juntas
      const nextAntes = direction === 'next' 
        ? (currentAntes + 1) % imagenesAntes.length
        : currentAntes === 0 ? imagenesAntes.length - 1 : currentAntes - 1;
      
      const nextDespues = direction === 'next'
        ? (currentDespues + 1) % imagenesDespues.length
        : currentDespues === 0 ? imagenesDespues.length - 1 : currentDespues - 1;
      
      setCurrentAntes(nextAntes);
      setCurrentDespues(nextDespues);
    }
  };

  // Navegación individual
  const navigateAntes = (direction) => {
    if (!syncNavigation) {
      const next = direction === 'next'
        ? (currentAntes + 1) % imagenesAntes.length
        : currentAntes === 0 ? imagenesAntes.length - 1 : currentAntes - 1;
      setCurrentAntes(next);
    }
  };

  const navigateDespues = (direction) => {
    if (!syncNavigation) {
      const next = direction === 'next'
        ? (currentDespues + 1) % imagenesDespues.length
        : currentDespues === 0 ? imagenesDespues.length - 1 : currentDespues - 1;
      setCurrentDespues(next);
    }
  };

  // Funciones para el visor de imágenes
  const openViewer = (images, startIndex = 0, type = 'all') => {
    setViewerImages(images);
    setViewerIndex(startIndex);
    setViewerOpen(true);
  };

  const closeViewer = () => {
    setViewerOpen(false);
  };

  // Función para abrir visor con imágenes específicas
  const openAntesViewer = () => {
    openViewer(imagenesAntes, currentAntes, 'antes');
  };

  const openDespuesViewer = () => {
    openViewer(imagenesDespues, currentDespues, 'despues');
  };

  // Función para abrir visor con todas las imágenes
  const openAllImagesViewer = () => {
    const allImages = [...imagenesAntes, ...imagenesDespues];
    const startIndex = currentAntes; // Empezar con la imagen actual "antes"
    openViewer(allImages, startIndex, 'all');
  };

  const imagenAntes = imagenesAntes[currentAntes];
  const imagenDespues = imagenesDespues[currentDespues];

  // Función para renderizar el contenido común
  const renderContent = () => (
    <>
      {/* Estilos CSS dinámicos para el slider */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .draggable-container.dragging {
            filter: brightness(1.1) saturate(1.1);
          }
          .draggable-container.dragging * {
            pointer-events: none;
          }
          .draggable-container:active {
            filter: brightness(1.05);
          }
        `
      }} />
      {/* Header con controles */}
      <div className={`${isFullscreen ? `relative ${isMobile ? 'flex flex-col gap-2' : 'flex items-center justify-between gap-2'} ${isMobile ? 'text-xs' : 'text-sm'}` : 'flex flex-col lg:flex-row justify-between items-center gap-4'}`}>
        {/* Texto descriptivo */}
        <div className={`${isFullscreen ? (isMobile ? 'text-center' : 'text-left flex-1') : 'text-center lg:text-left'}`}>
          <p className={`text-gray-400 ${isFullscreen && isMobile ? 'text-xs' : 'text-sm'}`}>
            Compare el estado inicial con el resultado final
          </p>
        </div>
        
        {/* Navegación de imágenes en fullscreen - solo desktop */}
        {isFullscreen && !isMobile && syncNavigation && (imagenesAntes.length > 1 || imagenesDespues.length > 1) && (
          <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center bg-gray-800 rounded-lg border border-gray-600">
            <button
              onClick={() => navigateImages('prev')}
              className="bg-gray-700 hover:bg-gray-600 text-white p-2 rounded-l-lg transition-colors duration-200 disabled:opacity-50"
              disabled={currentAntes === 0 && currentDespues === 0}
              title="Imagen anterior"
            >
              <FaChevronLeft size={12} />
            </button>
            
            <div className="bg-gray-800 px-4 py-2 text-center border-x border-gray-600 min-w-[50px]">
              <div className="text-white text-xs font-medium whitespace-nowrap">
                {Math.min(currentAntes + 1, currentDespues + 1)} / {Math.min(imagenesAntes.length, imagenesDespues.length)}
              </div>
            </div>
            
            <button
              onClick={() => navigateImages('next')}
              className="bg-gray-700 hover:bg-gray-600 text-white p-2 rounded-r-lg transition-colors duration-200 disabled:opacity-50"
              disabled={currentAntes === imagenesAntes.length - 1 && currentDespues === imagenesDespues.length - 1}
              title="Imagen siguiente"
            >
              <FaChevronRight size={12} />
            </button>
          </div>
        )}
        
        {/* Controles principales */}
        <div className={`flex ${isFullscreen ? (isMobile ? 'flex-col gap-2 w-full' : 'flex-row') : 'flex-col sm:flex-row'} items-center gap-2`}>
          {/* Modo de comparación - ancho completo en móvil fullscreen */}
          <div className={`flex space-x-1 bg-gray-800 border border-gray-600 rounded-lg p-1 ${isFullscreen && isMobile ? 'w-full' : ''}`}>
            <button
              onClick={() => setComparisonMode('slider')}
              className={`
                flex items-center justify-center gap-2 py-1.5 px-3 rounded-lg 
                text-xs font-medium transition-all duration-200 border
                ${isFullscreen && isMobile ? 'flex-1' : ''}
                ${comparisonMode === 'slider'
                  ? 'bg-blue-600 text-white border-blue-500 shadow-lg'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700 border-transparent hover:border-gray-600'
                }
              `}
              title="Vista con slider"
            >
              <FaExpandArrowsAlt className="text-sm" />
              <span>Slider</span>
            </button>
            <button
              onClick={() => setComparisonMode('sideBySide')}
              className={`
                flex items-center justify-center gap-2 py-1.5 px-3 rounded-lg 
                text-xs font-medium transition-all duration-200 border
                ${isFullscreen && isMobile ? 'flex-1' : ''}
                ${comparisonMode === 'sideBySide'
                  ? 'bg-blue-600 text-white border-blue-500 shadow-lg'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700 border-transparent hover:border-gray-600'
                }
              `}
              title="Vista lado a lado"
            >
              <FaEye className="text-sm" />
              <span>Lado a lado</span>
            </button>
          </div>

          {/* Fila con navegador centrado entre switch y fullscreen */}
          <div className={`flex items-center ${isFullscreen && isMobile ? 'justify-between w-full' : 'gap-2'}`}>
            {/* Navegación sincronizada con switch */}
            <div className="flex items-center gap-2 bg-gray-800 border border-gray-600 rounded-lg px-3 py-1.5">
              <span className="text-gray-400 text-xs font-medium whitespace-nowrap">Sincronizar</span>
              <Switch
                checked={syncNavigation}
                onChange={setSyncNavigation}
                size="sm"
              />
            </div>

            {/* Navegador centrado - solo en móvil fullscreen */}
            {isFullscreen && isMobile && syncNavigation && (imagenesAntes.length > 1 || imagenesDespues.length > 1) && (
              <div className="flex items-center bg-gray-800 rounded-lg border border-gray-600">
                <button
                  onClick={() => navigateImages('prev')}
                  className="bg-gray-700 hover:bg-gray-600 text-white px-2 py-1.5 rounded-l-lg transition-colors duration-200 disabled:opacity-50"
                  disabled={currentAntes === 0 && currentDespues === 0}
                  title="Imagen anterior"
                >
                  <FaChevronLeft size={10} />
                </button>
                
                <div className="bg-gray-800 px-2 py-1.5 text-center border-x border-gray-600 min-w-[40px]">
                  <div className="text-white text-xs font-medium whitespace-nowrap">
                    {Math.min(currentAntes + 1, currentDespues + 1)}/{Math.min(imagenesAntes.length, imagenesDespues.length)}
                  </div>
                </div>
                
                <button
                  onClick={() => navigateImages('next')}
                  className="bg-gray-700 hover:bg-gray-600 text-white px-2 py-1.5 rounded-r-lg transition-colors duration-200 disabled:opacity-50"
                  disabled={currentAntes === imagenesAntes.length - 1 && currentDespues === imagenesDespues.length - 1}
                  title="Imagen siguiente"
                >
                  <FaChevronRight size={10} />
                </button>
              </div>
            )}

            {/* Botón de pantalla completa - solo en modo normal */}
            {!isFullscreen && (
              <button
                onClick={toggleFullscreen}
                className="p-2 bg-gray-800 border border-gray-600 rounded-lg hover:bg-gray-700 hover:border-gray-500 transition-all duration-200 text-gray-400 hover:text-white"
                title="Pantalla completa"
              >
                <FaExpand size={16} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Área de comparación */}
      <div className={`bg-gray-800 overflow-hidden ${
        isFullscreen ? 'rounded-lg' : 
        (syncNavigation && (imagenesAntes.length > 1 || imagenesDespues.length > 1)) ? 'rounded-t-lg' : 
        'rounded-lg'
      }`}>
        {comparisonMode === 'slider' && (
          <div className={`relative ${isFullscreen ? `aspect-auto ${isMobile ? 'h-[80vh]' : 'h-[85vh]'} min-h-96 rounded-b-lg` : 'aspect-video'} bg-gray-900 select-none overflow-hidden`}>
            <div
              ref={containerRef}
              className="relative w-full h-full overflow-hidden rounded-lg cursor-col-resize transition-all duration-150 ease-out hover:brightness-110 draggable-container"
              onMouseDown={handleSliderStart}
              onTouchStart={handleSliderStart}
              onClick={handleSliderClick}
              style={{
                touchAction: 'none', // Prevenir scroll en mobile durante drag
                willChange: 'filter', // Optimizar para cambios de filtro
                backfaceVisibility: 'hidden', // Optimizar rendering
                perspective: '1000px' // Habilitar aceleración 3D
              }}
            >
              {/* Imagen Posterior (fondo) */}
              <img
                src={imagenDespues?.url_storage}
                alt="Posterior"
                className="absolute inset-0 w-full h-full object-cover"
                style={{
                  willChange: 'auto',
                  backfaceVisibility: 'hidden'
                }}
                loading="eager"
                decoding="async"
              />
              
              {/* Imagen Previa (con clip) */}
              <div
                className="absolute inset-0 overflow-hidden"
                style={{ 
                  clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`,
                  willChange: 'clip-path',
                  backfaceVisibility: 'hidden',
                  transform: 'translateZ(0)' // Forzar layer de composición
                }}
              >
                <img
                  src={imagenAntes?.url_storage}
                  alt="Previo"
                  className="w-full h-full object-cover"
                  style={{
                    willChange: 'auto',
                    backfaceVisibility: 'hidden'
                  }}
                  loading="eager"
                  decoding="async"
                />
              </div>
              
              {/* Línea divisoria y handle mejorados */}
              <div
                className="absolute top-0 bottom-0 w-1 bg-white shadow-xl z-10 pointer-events-none border-l-2 border-r-2 border-gray-300"
                style={{ 
                  left: `${sliderPosition}%`,
                  transform: 'translateX(-50%) translateZ(0)',
                  background: 'linear-gradient(to bottom, rgba(255,255,255,0.9), rgba(255,255,255,1), rgba(255,255,255,0.9))',
                  willChange: 'transform',
                  backfaceVisibility: 'hidden',
                  borderColor: 'rgba(0, 0, 0, 0.2)'
                }}
              >
                {/* Handle principal */}
                <div 
                  className="absolute top-1/2 left-1/2 bg-white rounded-full p-3 shadow-2xl border-2 border-gray-300 transition-all duration-200 hover:scale-110 hover:bg-blue-50"
                  style={{
                    transform: 'translate(-50%, -50%) translateZ(0)',
                    willChange: 'transform',
                    backfaceVisibility: 'hidden'
                  }}
                >
                  <FaExpandArrowsAlt className="text-gray-700" size={14} />
                </div>
                
                {/* Indicadores de dirección */}
                <div 
                  className="absolute top-4 left-1/2 bg-white/90 rounded-full px-2 py-1 shadow-lg"
                  style={{
                    transform: 'translateX(-50%) translateZ(0)',
                    backfaceVisibility: 'hidden'
                  }}
                >
                  <span className="text-xs font-medium text-gray-700">←→</span>
                </div>
                
                {/* Líneas de guía */}
                <div 
                  className="absolute top-0 left-1/2 w-0.5 h-8 bg-white/60"
                  style={{
                    transform: 'translateX(-50%) translateZ(0)',
                    backfaceVisibility: 'hidden'
                  }}
                ></div>
                <div 
                  className="absolute bottom-0 left-1/2 w-0.5 h-8 bg-white/60"
                  style={{
                    transform: 'translateX(-50%) translateZ(0)',
                    backfaceVisibility: 'hidden'
                  }}
                ></div>
              </div>

              {/* Etiquetas */}
              {showOverlay && (
                <>
                  <div className="absolute top-4 left-4 bg-blue-600 text-white px-3 py-1 rounded text-sm font-medium">
                    PREVIO
                  </div>
                  <div className="absolute top-4 right-4 bg-green-600 text-white px-3 py-1 rounded text-sm font-medium">
                    POSTERIOR
                  </div>
                </>
              )}
              
              {/* Tamaños de archivo en esquinas inferiores */}
              {imagenAntes?.tamaño_kb && (
                <div className="absolute bottom-4 left-4 bg-black/70 text-white px-2 py-1 rounded text-xs">
                  {imagenAntes.tamaño_kb}KB
                </div>
              )}
              {imagenDespues?.tamaño_kb && (
                <div className="absolute bottom-4 right-4 bg-black/70 text-white px-2 py-1 rounded text-xs">
                  {imagenDespues.tamaño_kb}KB
                </div>
              )}
            </div>
          </div>
        )}

        {comparisonMode === 'sideBySide' && (
          <div className={`grid ${isFullscreen && isMobile ? 'grid-cols-1 gap-2' : 'grid-cols-1 md:grid-cols-2 gap-4'} p-4 ${isFullscreen ? `${isMobile ? 'min-h-[75vh]' : 'min-h-[80vh]'}` : ''}`}>
            {/* Lado Previo */}
            <div className="relative">
              <div className={`${isFullscreen ? `aspect-auto ${isMobile ? 'h-[35vh]' : 'h-[75vh]'} min-h-80` : 'aspect-video'} bg-gray-900 rounded-lg overflow-hidden relative group cursor-pointer`}
                   onClick={openAntesViewer}>
                <img
                  src={imagenAntes?.url_storage}
                  alt="Antes"
                  className="w-full h-full object-cover transition-transform group-hover:scale-105"
                />
                <div className="absolute top-3 left-3 bg-blue-600 text-white px-2 py-1 rounded text-sm font-medium">
                  PREVIO
                </div>
                
                {/* Tamaño de archivo */}
                {imagenAntes?.tamaño_kb && (
                  <div className="absolute bottom-3 right-3 bg-black/70 text-white px-2 py-1 rounded text-xs">
                    {imagenAntes.tamaño_kb}KB
                  </div>
                )}
                
                {/* Overlay con botón de visor al hover */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <div className="flex flex-col items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openAntesViewer();
                      }}
                      className="p-3 bg-blue-600 hover:bg-blue-700 rounded-full transition-colors"
                      title="Ver en visor completo"
                    >
                      <FaSearchPlus className="text-white" size={16} />
                    </button>
                    <span className="text-white text-xs font-medium">Ver imagen</span>
                  </div>
                </div>
                
                {/* Navegación individual para Previo */}
                {!syncNavigation && imagenesAntes.length > 1 && (
                  <div className="absolute inset-0 flex items-center justify-between p-2 pointer-events-none">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigateAntes('prev');
                      }}
                      className="bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 pointer-events-auto"
                    >
                      <FaChevronLeft size={12} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigateAntes('next');
                      }}
                      className="bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 pointer-events-auto"
                    >
                      <FaChevronRight size={12} />
                    </button>
                  </div>
                )}
              </div>
              
              {/* Info de imagen Previa */}
              <div className="mt-2 text-center">
                <p className="text-blue-300 text-sm">
                  {currentAntes + 1} de {imagenesAntes.length}
                </p>
                {imagenAntes?.descripcion && (
                  <p className="text-gray-400 text-xs mt-1 truncate">
                    {imagenAntes.descripcion}
                  </p>
                )}
              </div>
            </div>

            {/* Lado POSTERIOR */}
            <div className="relative">
              <div className={`${isFullscreen ? `aspect-auto ${isMobile ? 'h-[35vh]' : 'h-[75vh]'} min-h-80` : 'aspect-video'} bg-gray-900 rounded-lg overflow-hidden relative group cursor-pointer`}
                   onClick={openDespuesViewer}>
                <img
                  src={imagenDespues?.url_storage}
                  alt="Después"
                  className="w-full h-full object-cover transition-transform group-hover:scale-105"
                />
                <div className="absolute top-3 left-3 bg-green-600 text-white px-2 py-1 rounded text-sm font-medium">
                  POSTERIOR
                </div>
                
                {/* Tamaño de archivo */}
                {imagenDespues?.tamaño_kb && (
                  <div className="absolute bottom-3 right-3 bg-black/70 text-white px-2 py-1 rounded text-xs">
                    {imagenDespues.tamaño_kb}KB
                  </div>
                )}
                
                {/* Overlay con botón de visor al hover */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <div className="flex flex-col items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openDespuesViewer();
                      }}
                      className="p-3 bg-green-600 hover:bg-green-700 rounded-full transition-colors"
                      title="Ver en visor completo"
                    >
                      <FaSearchPlus className="text-white" size={16} />
                    </button>
                    <span className="text-white text-xs font-medium">Ver imagen</span>
                  </div>
                </div>
                
                {/* Navegación individual para Posterior */}
                {!syncNavigation && imagenesDespues.length > 1 && (
                  <div className="absolute inset-0 flex items-center justify-between p-2 pointer-events-none">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigateDespues('prev');
                      }}
                      className="bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 pointer-events-auto"
                    >
                      <FaChevronLeft size={12} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigateDespues('next');
                      }}
                      className="bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 pointer-events-auto"
                    >
                      <FaChevronRight size={12} />
                    </button>
                  </div>
                )}
              </div>
              
              {/* Info de imagen Posterior */}
              <div className="mt-2 text-center">
                <p className="text-green-300 text-sm">
                  {currentDespues + 1} de {imagenesDespues.length}
                </p>
                {imagenDespues?.descripcion && (
                  <p className="text-gray-400 text-xs mt-1 truncate">
                    {imagenDespues.descripcion}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}


        {/* Navegación sincronizada mejorada - solo en modo normal */}
        {!isFullscreen && syncNavigation && (imagenesAntes.length > 1 || imagenesDespues.length > 1) && (
          <div className="flex justify-center items-center p-4 border-t border-gray-700 rounded-b-lg">
            <div className="flex items-center bg-gray-800 rounded-xl border border-gray-600 shadow-lg">
              <button
                onClick={() => navigateImages('prev')}
                className="bg-gray-700 hover:bg-gray-600 text-white p-3 rounded-l-xl transition-colors duration-200 disabled:opacity-50"
                disabled={currentAntes === 0 && currentDespues === 0}
                title="Imagen anterior"
              >
                <FaChevronLeft size={14} />
              </button>
              
              <div className="bg-gray-800 px-6 py-3 text-center border-x border-gray-600">
                <div className="text-white text-sm font-medium">
                  Imagen {Math.min(currentAntes + 1, currentDespues + 1)} de {Math.min(imagenesAntes.length, imagenesDespues.length)}
                </div>
                <div className="text-gray-400 text-xs mt-1">
                  Navegación sincronizada
                </div>
              </div>
              
              <button
                onClick={() => navigateImages('next')}
                className="bg-gray-700 hover:bg-gray-600 text-white p-3 rounded-r-xl transition-colors duration-200 disabled:opacity-50"
                disabled={currentAntes === imagenesAntes.length - 1 && currentDespues === imagenesDespues.length - 1}
                title="Imagen siguiente"
              >
                <FaChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>


      {/* Instrucciones de uso */}
      {comparisonMode === 'slider' && (
        <div className="text-center text-gray-400 text-xs">
          {isMobile 
            ? 'Toca y arrastra para comparar las imágenes'
            : 'Haz clic y arrastra para comparar las imágenes'
          }
        </div>
      )}

      {/* Instrucciones de pantalla completa */}
      {isFullscreen && (
        <div className="text-center text-gray-400 text-xs">
          {isMobile ? (
            <span>Toca el botón <FaTimes className="inline mx-1" /> para salir de pantalla completa</span>
          ) : (
            <span>Presiona <kbd className="bg-gray-700 px-1 py-0.5 rounded text-white">ESC</kbd> o el botón <FaTimes className="inline mx-1" /> para salir</span>
          )}
        </div>
      )}

      {/* Visor de imágenes */}
      <ImageViewer
        images={viewerImages}
        isOpen={viewerOpen}
        onClose={closeViewer}
        initialIndex={viewerIndex}
        title="Comparación de Imágenes"
        style={{ zIndex: 9999, marginTop: 0 }}
      />
    </>
  );

  // Renderizar en modo normal o fullscreen
  if (isFullscreen) {
    const fullscreenContent = (
      <div 
        className="fixed inset-0 bg-gray-900 flex flex-col overflow-auto"
        style={{ 
          zIndex: 9999, 
          width: '100vw', 
          height: '100vh',
          position: 'fixed',
          top: 0,
          left: 0
        }}
      >
        {/* Botón cerrar en esquina superior derecha */}
        <button
          type="button"
          onClick={() => setIsFullscreen(false)}
          className="absolute top-4 right-4 z-20 p-2 hover:bg-white/20 rounded-lg transition-colors text-white"
          title="Cerrar pantalla completa (ESC)"
        >
          <FaTimes size={18} />
        </button>

        <div className="flex-1 p-4 pt-10 space-y-4">
          {renderContent()}
        </div>
      </div>
    );
    
    return createPortal(fullscreenContent, document.body);
  }

  // Modo normal
  return (
    <div className={`space-y-4 ${className}`}>
      {renderContent()}
    </div>
  );
};

export default ImageComparison;